/**
 * The audible proximity alert: a click track that speeds up as the boat closes.
 *
 * A Geiger counter, borrowed for the same reason it works there — you can hear
 * a rate change without looking at anything, and it carries a magnitude, which a
 * beep does not. The whole point is that it is usable with the phone face down
 * in a cockpit locker while both hands are on the wheel.
 *
 * Three things are worth knowing before touching this file.
 *
 * **Silence never means clear water.** The scan behind it reads rendered chart
 * tiles, so it can only hear about what is loaded and on screen. That warning
 * belongs on every surface that offers this — see Settings → Display.
 *
 * **Browsers will not make a sound until the user has interacted with the page.**
 * An `AudioContext` created before a gesture starts `suspended` and stays that
 * way, playing nothing and reporting no error. That is why the sink exposes
 * `ready`, why the settings toggle and the Test button both resume it, and why
 * the UI can say "tap Test" rather than leaving the skipper to wonder whether
 * the alarm is broken or the water is clear. The distinction matters here more
 * than in an ordinary app.
 *
 * **The scheduling and the noise-making are separate on purpose.** `ClickSink`
 * is the whole interface to Web Audio, so `ProximityAlarm` — the part with the
 * timing rules in it — is testable with a recording stub and fake timers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { clickFrequencyHz, clickIntervalMs, type ProximityAlert } from '../core/proximity';

/** Everything the alarm needs from an audio backend. */
export interface ClickSink {
  /** True when a click would actually be heard. False before a user gesture. */
  readonly ready: boolean;
  /** Play one click now. */
  click(frequencyHz: number, volume: number): void;
  /** Ask the browser to start audio. Must be called from a user gesture. */
  resume(): Promise<void>;
  /** Release the audio hardware. */
  close(): void;
}

/**
 * Length of the click, seconds.
 *
 * Twelve milliseconds. Long enough to have a pitch, short enough that fourteen
 * a second stay separate clicks rather than merging into a buzz — the rate is
 * the information, so they must not run together at the top of the scale.
 */
const CLICK_SECONDS = 0.012;

/** Decay constant of the click envelope, seconds. */
const CLICK_DECAY_S = 0.0018;

type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;

function audioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null;
  const scope = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

/** True when this browser can play the alert at all. */
export function isAlarmSupported(): boolean {
  return audioContextConstructor() !== null;
}

/**
 * A click sink backed by Web Audio, or null where there is no Web Audio.
 *
 * The click is filtered noise rather than a tone burst: a pure sine pip at this
 * length sounds like a fault, and noise cuts through wind and engine far better
 * at the same loudness. One buffer is generated up front and replayed, because
 * allocating a buffer fourteen times a second on a phone is not free.
 */
export function createWebAudioClickSink(): ClickSink | null {
  const Ctor = audioContextConstructor();
  if (!Ctor) return null;

  let context: AudioContext;
  try {
    context = new Ctor({ latencyHint: 'interactive' });
  } catch {
    return null;
  }

  const frames = Math.max(1, Math.ceil(context.sampleRate * CLICK_SECONDS));
  const noise = context.createBuffer(1, frames, context.sampleRate);
  const samples = noise.getChannelData(0);
  for (let i = 0; i < frames; i++) {
    // Exponential decay, so the click has an attack and no tail. A flat burst
    // of noise reads as static; this reads as a tick.
    samples[i] = (Math.random() * 2 - 1) * Math.exp(-i / (context.sampleRate * CLICK_DECAY_S));
  }

  return {
    get ready(): boolean {
      return context.state === 'running';
    },
    click(frequencyHz: number, volume: number): void {
      if (context.state !== 'running') return;
      const source = context.createBufferSource();
      source.buffer = noise;

      const filter = context.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = frequencyHz;
      filter.Q.value = 1.4;

      const gain = context.createGain();
      // Squared, because loudness is not linear in amplitude and a volume
      // slider that does nothing for its first half is worse than no slider.
      gain.gain.value = Math.min(1, Math.max(0, volume)) ** 2;

      source.connect(filter);
      filter.connect(gain);
      gain.connect(context.destination);
      source.start();
      // Nodes are garbage once the buffer has played; dropping the references
      // here keeps a long watch from accumulating thousands of them.
      source.onended = (): void => {
        source.disconnect();
        filter.disconnect();
        gain.disconnect();
      };
    },
    async resume(): Promise<void> {
      if (context.state !== 'running') await context.resume();
    },
    close(): void {
      void context.close();
    },
  };
}

/**
 * Turns an alert level into a stream of clicks.
 *
 * The next click is scheduled from the time of the *last* one rather than from
 * now, so a level that updates several times a second — which it does, the scan
 * runs on every map idle — reschedules the rate instead of firing a click on
 * every update. Getting this wrong produces a burst of noise whenever the boat
 * moves, which sounds exactly like danger and means nothing.
 */
export class ProximityAlarm {
  private timer: ReturnType<typeof setTimeout> | null = null;

  private level = 0;

  private severity: ProximityAlert['severity'] = null;

  private volume = 1;

  private lastClickAt = 0;

  constructor(private readonly sink: ClickSink) {}

  /** Set the current alert. Starts, stops or re-rates the click track. */
  setAlert(level: number, severity: ProximityAlert['severity'], volume: number): void {
    this.level = level;
    this.severity = severity;
    this.volume = volume;
    this.schedule();
  }

  /** Stop clicking, keeping the sink open for the next alert. */
  stop(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.level = 0;
    this.severity = null;
  }

  /** One click at the current level, whatever the schedule is doing. */
  test(): void {
    this.sink.click(clickFrequencyHz(this.severity, this.level), this.volume);
  }

  /** Stop and release the audio hardware. */
  dispose(): void {
    this.stop();
    this.sink.close();
  }

  private schedule(): void {
    const interval = clickIntervalMs(this.level);
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (interval === null) return;

    const due = this.lastClickAt + interval;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.lastClickAt = Date.now();
      this.sink.click(clickFrequencyHz(this.severity, this.level), this.volume);
      this.schedule();
    }, Math.max(0, due - Date.now()));
  }
}

export interface AlarmStatus {
  /** False where the browser has no Web Audio at all. */
  supported: boolean;
  /**
   * Audio exists, but the browser has not allowed it to start.
   *
   * Worth surfacing rather than swallowing: an alarm that is silent because it
   * was never unblocked is indistinguishable, from the helm, from an alarm that
   * is silent because there is nothing ahead.
   */
  blocked: boolean;
  /** Play one click. Also the gesture that unblocks audio. */
  test: () => void;
}

/**
 * Drive the alarm from an alert level.
 *
 * The context is created when the alert is switched on — which happens inside
 * the click that switched it on, and is therefore a user gesture the browser
 * will accept. Turning it off disposes it, so a boat sitting at anchor with the
 * alarm off is not holding the audio hardware open.
 */
export function useProximityAlarm(
  enabled: boolean,
  alert: Pick<ProximityAlert, 'level' | 'severity'>,
  volume: number,
): AlarmStatus {
  const [supported] = useState(isAlarmSupported);
  const [blocked, setBlocked] = useState(false);
  const sink = useRef<ClickSink | null>(null);
  const alarm = useRef<ProximityAlarm | null>(null);

  const ensure = useCallback((): ProximityAlarm | null => {
    if (alarm.current) return alarm.current;
    const created = createWebAudioClickSink();
    if (!created) return null;
    sink.current = created;
    alarm.current = new ProximityAlarm(created);
    return alarm.current;
  }, []);

  const unblock = useCallback((): void => {
    const current = sink.current;
    if (!current) return;
    void current.resume()
      .then(() => setBlocked(!current.ready))
      .catch(() => setBlocked(true));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!ensure()) return undefined;
    unblock();
    return () => {
      alarm.current?.dispose();
      alarm.current = null;
      sink.current = null;
      setBlocked(false);
    };
  }, [enabled, ensure, unblock]);

  useEffect(() => {
    if (!alarm.current) return;
    alarm.current.setAlert(enabled ? alert.level : 0, alert.severity, volume);
    // Only worth chasing when there is something to say. Retrying on every
    // quiet update would ask the browser to start audio hundreds of times a
    // watch and never learn anything from the answer.
    if (enabled && alert.level > 0 && sink.current && !sink.current.ready) unblock();
  }, [enabled, alert.level, alert.severity, volume, unblock]);

  // Backgrounding the app suspends the context. Coming back must not leave a
  // silent alarm behind — the skipper has no way to tell that apart from clear
  // water.
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;
    const onVisible = (): void => { if (!document.hidden) unblock(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, unblock]);

  const test = useCallback((): void => {
    const instance = ensure();
    if (!instance || !sink.current) return;
    const current = sink.current;
    void current.resume()
      .then(() => {
        setBlocked(!current.ready);
        instance.test();
      })
      .catch(() => setBlocked(true));
  }, [ensure]);

  return { supported, blocked, test };
}
